/**
 * Register Page
 * Multi-step registration for studio owners
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, type RegisterData } from '../../context/AuthContext';
import { PlacesAutocomplete, type PlaceResult } from '../../components/events/PlacesAutocomplete';
import { checkEmail, checkPhone, activateDormant, type DormantMatch } from '../../api/auth';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { formatPhoneDisplay } from '../../utils/phone';
import { isSafeRedirect } from '../../utils/redirect';
import SmsConsentCheckbox from '../../components/ui/SmsConsentCheckbox';
import { isValidPostalCode } from '@sda/shared';

type Step = 'account' | 'studio' | 'address';

interface FormData extends RegisterData {
  confirmPassword: string;
}

const STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  // Canadian provinces/territories — let Canadian studios pick their province (SDA-197)
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { register, login, isLoading } = useAuth();
  // Post-registration destination: router state (in-app redirects) or the
  // ?redirect= param (email links, e.g. the SDA-265 balance-payment request,
  // which sends dormant studios here to activate before paying a booking).
  // isSafeRedirect rejects external URLs and auth self-loops incl. /register.
  const rawFrom = (location.state?.from?.pathname as string | undefined) || searchParams.get('redirect');
  const from = isSafeRedirect(rawFrom) ? rawFrom : undefined;

  const [step, setStep] = useState<Step>('account');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dormantMatch, setDormantMatch] = useState<DormantMatch | null>(null);

  const [formData, setFormData] = useState<FormData>({
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    studio_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    google_place_id: '',
  });
  // Optional SMS/text-message opt-in (A2P/10DLC) — never blocks submit.
  const [smsConsent, setSmsConsent] = useState(false);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setIsDuplicateEmail(false);
  };

  const prefilledClass = (field: string) =>
    dormantMatch && formData[field as keyof FormData]
      ? 'input w-full bg-green-50 border-green-200'
      : 'input w-full';

  const validateStep = (): boolean => {
    switch (step) {
      case 'account':
        if (!formData.email || !formData.password || !formData.confirmPassword) {
          setError('Please fill in all required fields');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (
          !/[A-Z]/.test(formData.password) ||
          !/[a-z]/.test(formData.password) ||
          !/[0-9]/.test(formData.password) ||
          !/[^A-Za-z0-9]/.test(formData.password)
        ) {
          setError('Password must include uppercase, lowercase, number, and special character');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        break;
      case 'studio':
        if (!formData.first_name || !formData.last_name || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        break;
      case 'address':
        if (!formData.studio_name || !formData.address_line_1 || !formData.city || !formData.state || !formData.zip) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!isValidPostalCode(formData.zip)) {
          setError('Please enter a valid US ZIP or Canadian postal code');
          return false;
        }
        break;
    }
    return true;
  };

  const checkEmailAvailability = async (email: string): Promise<boolean> => {
    const { available, dormant_match } = await checkEmail(email);

    if (dormant_match) {
      const match = dormant_match;
      setDormantMatch(match);
      setFormData((prev) => ({
        ...prev,
        first_name: match.contact_first_name || prev.first_name,
        last_name: match.contact_last_name || prev.last_name,
        phone: match.contact_phone || prev.phone,
        studio_name: match.studio_name || prev.studio_name,
        address_line_1: match.address_line1 || prev.address_line_1,
        city: match.city || prev.city,
        state: match.state || prev.state,
        zip: match.zip_code || prev.zip,
      }));
      setManualEntry(true);
      return true; // Allow proceeding — this is an activation, not a duplicate
    }

    return available;
  };

  // When the user arrives with a prefilled ?email= (e.g. redirected here from a
  // failed login or a forgot-password attempt on a not-yet-activated account),
  // run the email check on mount so the "Activate Your Account" banner appears
  // immediately. Runs once; the normal (no-prefill) register path is untouched.
  useEffect(() => {
    const prefilledEmail = searchParams.get('email');
    if (prefilledEmail && prefilledEmail.includes('@')) {
      void checkEmailAvailability(prefilledEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = async () => {
    if (!validateStep()) return;
    setError(null);
    if (step === 'account') {
      const available = await checkEmailAvailability(formData.email);
      if (!available) {
        setIsDuplicateEmail(true);
        setError(null);
        return;
      }
      setStep('studio');
    } else if (step === 'studio') {
      // Check phone for dormant match only if we don't already have one from email
      if (!dormantMatch && formData.phone) {
        try {
          const phoneResult = await checkPhone(formData.phone);
          if (phoneResult.dormant_match) {
            const match = phoneResult.dormant_match;
            setDormantMatch(match);
            setFormData((prev) => ({
              ...prev,
              studio_name: match.studio_name || prev.studio_name,
              address_line_1: match.address_line1 || prev.address_line_1,
              city: match.city || prev.city,
              state: match.state || prev.state,
              zip: match.zip_code || prev.zip,
            }));
            setManualEntry(true);
          }
        } catch {
          // Don't block on phone check failure
        }
      }
      setStep('address');
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 'studio') setStep('account');
    else if (step === 'address') setStep('studio');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // On intermediate steps, Enter key should advance — not attempt registration
    if (step !== 'address') {
      handleNext();
      return;
    }
    if (!validateStep()) return;
    // In-flight lock: a rapid double-submit fires activateDormant twice — the
    // second call 400s ALREADY_ACTIVATED and flashes a confusing error while
    // the first is still completing. useAuth().isLoading doesn't cover the
    // direct activateDormant/register API calls, so we track our own flag.
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (dormantMatch) {
        // Activate dormant account instead of creating new
        const result = await activateDormant({
          studio_id: dormantMatch.studio_id,
          email: formData.email,
          password: formData.password,
        });
        if (result.session) {
          localStorage.setItem('sda_auth_tokens', JSON.stringify(result.session));
          // Auto-login to populate auth context
          try {
            await login(formData.email, formData.password);
          } catch {
            // Tokens are already stored, navigate anyway
          }
          navigate(from || '/dashboard', { replace: true });
          return;
        }
      } else {
        const { confirmPassword: _, ...registerData } = formData;
        // Send undefined (not '') when no city was picked — the schema's
        // google_place_id is .min(1).optional() and would reject an empty string.
        await register({ ...registerData, google_place_id: registerData.google_place_id || undefined, sms_consent: smsConsent });
        // Auto-login immediately — email is auto-confirmed, no verification gate
        try {
          await login(formData.email, formData.password);
          navigate(from || '/dashboard', { replace: true });
        } catch {
          // Auto-login failed — fall back to success page with manual login link
          navigate('/register/success', {
            replace: true,
            state: { email: formData.email, from: from ? { pathname: from } : undefined },
          });
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      if (message.toLowerCase().includes('already exists') || message.toLowerCase().includes('email_exists')) {
        setIsDuplicateEmail(true);
        setError(null);
        setStep('account');
      } else {
        setError(getFriendlyErrorMessage(err, 'Registration failed'));
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [manualEntry, setManualEntry] = useState(false);
  const studioFoundRef = useRef(false);

  const steps: { id: Step; label: string; number: number }[] = [
    { id: 'account', label: 'Account', number: 1 },
    { id: 'studio', label: 'About You', number: 2 },
    { id: 'address', label: 'Studio', number: 3 },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === step);

  const handleStudioPlaceSelected = useCallback((place: PlaceResult) => {
    studioFoundRef.current = true;
    if (place.name) updateField('studio_name', place.name);
    if (place.addressComponents?.street) updateField('address_line_1', place.addressComponents.street);
    if (place.addressComponents?.city) updateField('city', place.addressComponents.city);
    if (place.addressComponents?.state) updateField('state', place.addressComponents.state);
    if (place.addressComponents?.zip) updateField('zip', place.addressComponents.zip);
    // The establishment search's place_id is the studio's, not the city's — clear
    // any city place_id so the studio links via name+state, not a wrong key.
    updateField('google_place_id', '');
    setManualEntry(true);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-warm-100">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-display font-bold text-warm-900 mb-2">
                {dormantMatch ? 'Activate Your Account' : 'Create Your Account'}
              </h1>
              <p className="text-warm-600">
                {dormantMatch
                  ? 'Complete the form below to activate your studio account'
                  : 'Join Stage Door Access to book Broadway masterclasses'}
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8">
              {steps.map((s, index) => (
                <div key={s.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                      index <= currentStepIndex
                        ? 'bg-primary-600 text-white'
                        : 'bg-warm-200 text-warm-500'
                    }`}
                  >
                    {s.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-1 mx-2 transition-colors ${
                        index < currentStepIndex ? 'bg-primary-600' : 'bg-warm-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Duplicate-email banner — shown when a register attempt collides with an existing account */}
            {isDuplicateEmail && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm" role="alert">
                <p className="font-semibold text-red-800">This email is already registered.</p>
                <p className="mt-1 text-red-700">
                  Would you like to{' '}
                  <Link to="/login" className="font-medium underline hover:text-red-900">
                    sign in instead
                  </Link>
                  , or try a different email address?
                </p>
              </div>
            )}

            {/* Generic error alert */}
            {error && !isDuplicateEmail && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                {error}
              </div>
            )}

            {/* Dormant Match Banner */}
            {dormantMatch && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-green-800">Welcome back! We found your studio.</p>
                    <p className="text-sm text-green-700 mt-0.5">
                      <strong>{dormantMatch.studio_name}</strong> in {dormantMatch.city}, {dormantMatch.state}
                    </p>
                    <p className="text-xs text-green-600 mt-1">Just set a password to activate your account. Your studio info is pre-filled below — you can update any details from your account settings after activation.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Step 1: Account */}
              {step === 'account' && (
                <>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-warm-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      onBlur={async () => {
                        if (formData.email && formData.email.includes('@')) {
                          await checkEmailAvailability(formData.email);
                        }
                      }}
                      className="input w-full"
                      placeholder="you@studio.com"
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-warm-700 mb-1.5">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        className="input w-full pr-12"
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-500 hover:text-warm-700"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-warm-500">
                      Must include uppercase, lowercase, number, and special character
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-warm-700 mb-1.5">
                      Confirm Password *
                    </label>
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => updateField('confirmPassword', e.target.value)}
                      className="input w-full"
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </>
              )}

              {/* Step 2: Studio Info */}
              {step === 'studio' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-warm-700 mb-1.5">
                        First Name *
                      </label>
                      <input
                        id="first_name"
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => updateField('first_name', e.target.value)}
                        className={prefilledClass('first_name')}
                        placeholder="Jane"
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-warm-700 mb-1.5">
                        Last Name *
                      </label>
                      <input
                        id="last_name"
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => updateField('last_name', e.target.value)}
                        className={prefilledClass('last_name')}
                        placeholder="Smith"
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-warm-700 mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', formatPhoneDisplay(e.target.value))}
                      className={prefilledClass('phone')}
                      placeholder="(555) 123-4567"
                      autoComplete="tel"
                      required
                    />
                    <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} />
                  </div>
                </>
              )}

              {/* Step 3: Studio & Address */}
              {step === 'address' && (
                <>
                  {/* Studio search — hidden when dormant match pre-filled fields */}
                  {!dormantMatch && (
                    <div>
                      <label className="block text-sm font-medium text-warm-700 mb-1.5">
                        Find your Studio, School, etc.
                      </label>
                      <PlacesAutocomplete
                        mode="studio"
                        onPlaceSelected={handleStudioPlaceSelected}
                        placeholder="Search your studio name or address..."
                        className="input w-full"
                      />
                      <p className="mt-1 text-xs text-warm-500">
                        {manualEntry
                          ? 'Review the details below and edit if needed'
                          : 'Or '}
                        {!manualEntry && (
                          <button
                            type="button"
                            onClick={() => setManualEntry(true)}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            enter your studio details manually
                          </button>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Manual fields (shown after search selection or manual toggle) */}
                  {manualEntry && (
                    <>
                      <div>
                        <label htmlFor="studio_name" className="block text-sm font-medium text-warm-700 mb-1.5">
                          Studio Name *
                        </label>
                        <input
                          id="studio_name"
                          type="text"
                          value={formData.studio_name}
                          onChange={(e) => updateField('studio_name', e.target.value)}
                          className={prefilledClass('studio_name')}
                          placeholder="Broadway Dreams Dance Academy"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="address_line_1" className="block text-sm font-medium text-warm-700 mb-1.5">
                          Street Address *
                        </label>
                        <input
                          id="address_line_1"
                          type="text"
                          value={formData.address_line_1}
                          onChange={(e) => updateField('address_line_1', e.target.value)}
                          className={prefilledClass('address_line_1')}
                          placeholder="123 Dance Street"
                          autoComplete="address-line1"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="address_line_2" className="block text-sm font-medium text-warm-700 mb-1.5">
                          Suite / Unit
                        </label>
                        <input
                          id="address_line_2"
                          type="text"
                          value={formData.address_line_2}
                          onChange={(e) => updateField('address_line_2', e.target.value)}
                          className="input w-full"
                          placeholder="Suite 100"
                          autoComplete="address-line2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-warm-700 mb-1.5">
                            City *
                          </label>
                          <PlacesAutocomplete
                            mode="city"
                            id="city"
                            value={formData.city}
                            onInputChange={(t) => {
                              updateField('city', t);
                              updateField('state', '');
                              updateField('google_place_id', '');
                            }}
                            onPlaceSelected={(p: PlaceResult) => {
                              updateField('city', p.addressComponents?.city || p.name || formData.city);
                              if (p.addressComponents?.state) updateField('state', p.addressComponents.state);
                              updateField('google_place_id', p.placeId || '');
                            }}
                            placeholder="Start typing a city…"
                            className={prefilledClass('city')}
                          />
                        </div>
                        <div>
                          <label htmlFor="state" className="block text-sm font-medium text-warm-700 mb-1.5">
                            State *
                          </label>
                          <select
                            id="state"
                            value={formData.state}
                            onChange={(e) => updateField('state', e.target.value)}
                            className={prefilledClass('state')}
                            required
                          >
                            <option value="">Select state</option>
                            {STATES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="w-1/2 pr-2">
                        <label htmlFor="zip" className="block text-sm font-medium text-warm-700 mb-1.5">
                          ZIP / Postal Code *
                        </label>
                        <input
                          id="zip"
                          type="text"
                          value={formData.zip}
                          onChange={(e) => updateField('zip', e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase().slice(0, 7))}
                          className={prefilledClass('zip')}
                          placeholder="10001 or K1C 6E7"
                          autoComplete="postal-code"
                          maxLength={7}
                          required
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-4 pt-2">
                {step !== 'account' && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="btn flex-1 py-3"
                  >
                    Back
                  </button>
                )}
                {step !== 'address' ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn-primary flex-1 py-3"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting}
                    className="btn-primary flex-1 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading || isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {dormantMatch ? 'Activating Account...' : 'Creating Account...'}
                      </span>
                    ) : (
                      dormantMatch ? 'Activate Account' : 'Create Account'
                    )}
                  </button>
                )}
              </div>
            </form>

            {/* Login Link */}
            <p className="mt-8 text-center text-warm-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-warm-500 text-sm">
        © {new Date().getFullYear()} Stage Door Access. All rights reserved.
      </footer>
    </div>
  );
}
